import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Search, ArrowUpRight, BookOpen } from "lucide-react";
import { Link } from "react-router-dom";
import { getBlog } from "../lib/adventureApi";
import { useSeo } from "../lib/seo";
import RALayout from "../components/ra/RALayout";

const MotionLink = motion(Link);

export default function Blog() {
  const [posts, setPosts] = useState(null);
  const [q, setQ] = useState("");

  useSeo({ title: "Journal | Responsible Adventures", description: "Stories, guides and tips on trekking, culture, festivals and wellness in Nepal and the Himalaya." });
  useEffect(() => { getBlog().then((d) => setPosts(d.filter((p) => p.recovery === "ok"))).catch(() => setPosts([])); }, []);

  const filtered = useMemo(() => {
    if (!posts) return [];
    const term = q.trim().toLowerCase();
    if (!term) return posts;
    return posts.filter((p) => p.title.toLowerCase().includes(term) || (p.keywords || []).some((k) => k.includes(term)));
  }, [posts, q]);

  return (
    <RALayout>
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-16">
        <p className="font-mono text-xs tracking-[0.25em] uppercase text-moss mb-4">The Journal</p>
        <h1 className="font-display font-extrabold text-4xl sm:text-6xl tracking-tight leading-[0.98]" data-testid="blog-title">
          Stories from the Himalaya
        </h1>
        <p className="mt-5 text-lg text-ink-secondary max-w-2xl leading-relaxed">
          Trekking guides, festivals, culture, wellness and responsible-travel advice from our team on the ground.
        </p>

        <div className="mt-8 relative max-w-md">
          <Search className="w-4 h-4 text-ink-muted absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search the journal…" data-testid="blog-search"
            className="w-full pl-9 pr-3 py-2.5 text-sm bg-white border border-sand-dark rounded-sm text-ink focus:outline-none focus:ring-2 focus:ring-terracotta" />
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        {posts === null ? (
          <div className="flex items-center text-ink-secondary py-20"><Loader2 className="w-5 h-5 animate-spin mr-2 text-terracotta" /> Loading journal…</div>
        ) : (
          <>
            <p className="font-mono text-xs text-ink-muted mb-5" data-testid="blog-count">{filtered.length} articles</p>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="blog-grid">
              {filtered.map((p, i) => (
                <MotionLink key={p.slug} to={`/blog/${p.slug}`}
                  data-testid={`blog-card-${p.slug}`}
                  initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 6) * 0.04 }}
                  className="group flex items-start gap-3 p-5 rounded-md border border-sand-dark bg-white hover:border-terracotta transition-colors">
                  <div className="w-9 h-9 rounded-sm bg-moss-light flex items-center justify-center shrink-0"><BookOpen className="w-4 h-4 text-moss" /></div>
                  <div className="flex-1">
                    <h3 className="font-display font-bold text-[15px] tracking-tight leading-snug group-hover:text-terracotta transition-colors">{p.title}</h3>
                    {p.excerpt && <p className="text-xs text-ink-muted mt-1.5 line-clamp-2">{p.excerpt}</p>}
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-ink-muted group-hover:text-terracotta transition-colors shrink-0" />
                </MotionLink>
              ))}
            </div>
          </>
        )}
      </section>
    </RALayout>
  );
}
